import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { Modal } from "@mui/material";
import Spinner from "../../../src/shared/assets/icons/Spinner.jsx";
import GlobalButton from "./GlobalButton.jsx";

const MinMaxFileUploader = ({
  initialFiles = [],
  onFilesUpload,
  typeOfFilesAllowed = "images/*",
  maxFiles = 1,
  maxFileSize = 2 * 1024 * 1024, // 2MB in bytes
}) => {
  const [files, setFiles] = useState([]);
  const fileInputRef = useRef(null);
  const [uploadedFiles, setUploadedFiles] = useState(
    maxFiles === 1 && typeof initialFiles === "string"
      ? initialFiles
        ? [initialFiles]
        : []
      : Array.isArray(initialFiles)
        ? initialFiles
        : [],
  );
  const [loading, setLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isImage, setIsImage] = useState(false);
  const [error, setError] = useState("");

  // useEffect(() => {
  //   if (maxFiles === 1) {
  //     setUploadedFiles(
  //       typeof initialFiles === "string" && initialFiles ? [initialFiles] : []
  //     );
  //   } else {
  //     setUploadedFiles(Array.isArray(initialFiles) ? initialFiles : []);
  //   }
  // }, [initialFiles, maxFiles]);

  useEffect(() => {
    if (maxFiles === 1) {
      const newValue =
        typeof initialFiles === "string" && initialFiles ? [initialFiles] : [];
      if (JSON.stringify(newValue) !== JSON.stringify(uploadedFiles)) {
        setUploadedFiles(newValue);
      }
    } else {
      const newValue = Array.isArray(initialFiles) ? initialFiles : [];
      if (JSON.stringify(newValue) !== JSON.stringify(uploadedFiles)) {
        setUploadedFiles(newValue);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFiles, maxFiles]);

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);

    // Check if total files exceed maxFiles limit
    if (newFiles.length + files.length > maxFiles) {
      setError(`You can only upload ${maxFiles} file(s) at a time`);
      return;
    }

    // Check file size for each file
    const oversizedFiles = newFiles.filter((file) => file.size > maxFileSize);
    if (oversizedFiles.length > 0) {
      setError(`File size should not exceed ${maxFileSize / (1024 * 1024)}MB`);
      return;
    }

    const updatedFiles = [...files, ...newFiles];
    setError("");
    setFiles(updatedFiles);
    handleUpload(updatedFiles);
  };
  const handleUpload = async (files) => {
    if (files.length === 0) return;

    // Double-check file sizes before upload (client-side validation)
    const oversizedFiles = files.filter((file) => file.size > maxFileSize);
    if (oversizedFiles.length > 0) {
      setError(
        `One or more files exceed ${maxFileSize / (1024 * 1024)}MB limit`,
      );
      return;
    }
    console.log("upload");

    const formData = new FormData();
    files.forEach((file) => {
      formData.append("files", file);
    });
    console.log("upload fuction call before loading");

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${process.env.REACT_APP_URL}/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        },
      );
      console.log("upload fuction call");
      if (response.data.success) {
        const uploadedUrls = response.data.data;
        const updatedUploadedFiles = [...uploadedFiles, ...uploadedUrls];

        if (maxFiles === 1) {
          setUploadedFiles([uploadedUrls[0]]);
          onFilesUpload(uploadedUrls[0]);
        } else {
          setUploadedFiles(updatedUploadedFiles);
          onFilesUpload(updatedUploadedFiles);
        }

        setFiles([]);
        if (fileInputRef.current) {
          fileInputRef.current.value = null;
        }
        setError("");
      }
    } catch (err) {
      console.error("Error uploading file", err);
      setError("Error uploading file. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  const handleDelete = (index) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    setFiles(updatedFiles);
    if (updatedFiles.length === 0 && fileInputRef.current) {
      fileInputRef.current.value = null;
    }
  };

  const handleDeleteUploaded = (index) => {
    const updatedUploadedFiles = uploadedFiles.filter((_, i) => i !== index);
    setUploadedFiles(updatedUploadedFiles);

    if (maxFiles === 1) {
      onFilesUpload(updatedUploadedFiles[0] || "");
    } else {
      onFilesUpload(updatedUploadedFiles);
    }

    if (updatedUploadedFiles.length === 0 && fileInputRef.current) {
      fileInputRef.current.value = null;
    }
  };

  const handleImageClick = (imageSrc) => {
    setSelectedImage(imageSrc);
    setIsImage(true);
  };

  return (
    <div className="file-uploader">
      <input
        ref={fileInputRef}
        type="file"
        accept={typeOfFilesAllowed}
        onChange={handleFileChange}
        className="my-2 p-2 w-[220px] md:w-full"
        multiple={maxFiles > 1}
        disabled={loading}
      />

      {error && <div className="text-red-500 text-sm mb-2">{error}</div>}

      {files.length > 0 && (
        <div className="file-list">
          <h3>Selected Files:</h3>

          {loading && (
            <div className="text-blue-500 text-sm font-medium mb-2 flex items-center gap-2">
              <Spinner />
              Uploading... Please wait
            </div>
          )}

          <ul className="flex gap-2 flex-wrap">
            {files.map((file, index) => (
              <li
                key={index}
                className="border p-3 my-2 flex items-center flex-col gap-2"
              >
                {file.type.startsWith("image/") ? (
                  <img
                    src={URL.createObjectURL(file)}
                    alt={file.name}
                    className="h-20 w-20 object-cover rounded"
                  />
                ) : (
                  <div className="h-20 w-20 bg-gray-100 flex items-center justify-center rounded">
                    <span className="text-sm text-gray-500">
                      {file.type.split("/")[1] || "File"}
                    </span>
                  </div>
                )}
                <span className="text-sm text-center max-w-[100px] truncate">
                  {file.name}
                </span>
                <span className="text-xs text-gray-500">
                  {(file.size / (1024 * 1024)).toFixed(2)} MB
                </span>

                <GlobalButton
                  variant="warning"
                  size="small"
                  disabled={loading}
                  onClick={() => handleDelete(index)}
                >
                  {`Remove`}
                </GlobalButton>
              </li>
            ))}
          </ul>
        </div>
      )}

      {uploadedFiles?.filter(Boolean).length > 0 && (
        <div className="uploaded-file-list">
          <h3>Uploaded Files:</h3>
          <ul className="flex gap-2 flex-wrap">
            {uploadedFiles?.filter(Boolean).map((fileUrl, index) => (
              <li
                key={index}
                className="border p-3 my-2 flex items-center flex-col gap-2"
              >
                {fileUrl.match(/\.(mp4|mov)$/i) ? (
                  <video
                    controls
                    className="h-20 w-20 object-cover rounded"
                    src={fileUrl}
                  />
                ) : fileUrl.match(/\.pdf$/i) ? (
                  <embed
                    src={fileUrl}
                    type="application/pdf"
                    className="h-20 w-20 object-cover rounded"
                  />
                ) : (
                  <img
                    src={fileUrl}
                    alt={`uploaded-${index}`}
                    className="h-20 w-20 object-cover rounded cursor-pointer"
                    onClick={() => handleImageClick(fileUrl)}
                  />
                )}

                <GlobalButton
                  variant="warning"
                  size="small"
                  onClick={() => handleDeleteUploaded(index)}
                >
                  {`Delete`}
                </GlobalButton>
              </li>
            ))}
          </ul>
        </div>
      )}

      <Modal
        open={isImage}
        onClose={() => {
          setIsImage(false);
          setSelectedImage(null);
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          className="h-full flex justify-center items-center mx-auto relative"
          onClick={(e) => e.stopPropagation()}
        >
          <img
            src={selectedImage}
            alt="Enlarged preview"
            className="object-contain max-w-[80vw] max-h-[90vh]"
          />
        </div>
      </Modal>
    </div>
  );
};

export default MinMaxFileUploader;
